/* CONTEXT:START
Wave: 1
Status: implemented
Stub-Type: mock-data
Dependencies: [existing submission display, submission.isIncludedInReport field]
Risk-Level: medium
Validation-Method: unit-test
Performance-Target: <2s
Last-Updated: 2025-08-08
CONTEXT:END */

import React, { useState, useCallback } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Edit, Delete, Visibility } from '@mui/icons-material';
import { Submission } from '../../types';
import { apiService } from '../../services/api';
import { useApiCall } from '../../hooks/useApiCall';
import { LoadingSpinner, RetryWrapper } from '../shared';
import { SubmissionWorkflowStatus } from '../SubmissionWorkflowStatus';

interface SubmissionListProps {
  submissions: Submission[];
  loading: boolean;
  onSubmissionUpdated: () => void;
}

const SubmissionList: React.FC<SubmissionListProps> = ({
  submissions,
  loading,
  onSubmissionUpdated,
}) => {
  const [editingSubmission, setEditingSubmission] = useState<Submission | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deleteSubmission, setDeleteSubmission] = useState<Submission | null>(null);
  const [viewingSubmission, setViewingSubmission] = useState<Submission | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Note: Report status fetching removed because regular users don't have access
  // Progress calculation now uses submission.isIncludedInReport field instead

  // Update submission API call
  const updateSubmissionCall = useCallback(() => {
    if (!editingSubmission) throw new Error('No submission to update');
    // Construct the expected ID format: {year}-{month}-{submissionId}
    const formattedId = `${editingSubmission.year}-${editingSubmission.month}-${editingSubmission.id}`;
    return apiService.updateSubmission(formattedId, {
      content: editContent.trim(),
    });
  }, [editingSubmission, editContent]);

  const { 
    loading: updateLoading, 
    error: updateError, 
    execute: executeUpdate 
  } = useApiCall(updateSubmissionCall, {
    onSuccess: () => {
      setEditingSubmission(null);
      setEditContent('');
      setValidationError(null);
      onSubmissionUpdated();
    }
  });

  // Delete submission API call
  const deleteSubmissionCall = useCallback(() => {
    if (!deleteSubmission) throw new Error('No submission to delete');
    // Construct the expected ID format: {year}-{month}-{submissionId}
    const formattedId = `${deleteSubmission.year}-${deleteSubmission.month}-${deleteSubmission.id}`;
    return apiService.deleteSubmission(formattedId);
  }, [deleteSubmission]);

  const { 
    loading: deleteLoading, 
    error: deleteError, 
    execute: executeDelete 
  } = useApiCall(deleteSubmissionCall, {
    onSuccess: () => {
      setDeleteSubmission(null);
      setValidationError(null);
      onSubmissionUpdated();
    }
  });

  const handleEditClick = (submission: Submission) => {
    setEditingSubmission(submission);
    setEditContent(submission.content);
    setValidationError(null);
  };

  const handleEditSubmit = async () => {
    if (!editingSubmission || !editContent.trim()) {
      setValidationError('Content is required');
      return;
    }

    if (editContent.length > 2000) {
      setValidationError('Content must be 2000 characters or less');
      return;
    }

    setValidationError(null);
    await executeUpdate();
  };

  const handleDeleteClick = (submission: Submission) => {
    setDeleteSubmission(submission);
    setValidationError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteSubmission) return;
    setValidationError(null);
    await executeDelete();
  };

  const handleViewClick = (submission: Submission) => {
    setViewingSubmission(submission);
  };

  const handleCloseDialogs = () => {
    setEditingSubmission(null);
    setDeleteSubmission(null);
    setViewingSubmission(null);
    setEditContent('');
    setValidationError(null);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canEditSubmission = (submission: Submission): boolean => {
    // In Wave 1, allow editing if not yet approved/rejected or included in report
    return !['approved', 'rejected', 'included_in_report'].includes(submission.workflowState || 'submitted');
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, width: '100%', minWidth: 'unset' }}>
        <LoadingSpinner message="Loading submissions..." />
      </Paper>
    );
  }

  if (submissions.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', width: '100%', minWidth: 'unset' }}>
        <Typography variant="body1" color="text.secondary">
          No submissions found for this month.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 3, width: '100%', minWidth: 'unset' }}>
        <Typography variant="h6" gutterBottom>
          Your Submissions ({submissions.length})
        </Typography>
        
        <List sx={{ '& .MuiListItem-root': { mb: 2 } }}>
          {submissions.map((submission) => (
            <ListItem
              key={submission.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                p: 3,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                flexDirection: 'column',
                alignItems: 'stretch'
              }}
            >
              {/* Header with type and date */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={submission.type}
                    color={submission.type === 'highlight' ? 'success' : 'warning'}
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(submission.createdAt)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={() => handleViewClick(submission)}
                    color="info"
                    size="small"
                    aria-label="View submission details"
                  >
                    <Visibility />
                  </IconButton>
                  {canEditSubmission(submission) && (
                    <IconButton
                      onClick={() => handleEditClick(submission)}
                      color="primary"
                      size="small"
                      aria-label="Edit submission"
                    >
                      <Edit />
                    </IconButton>
                  )}
                  {canEditSubmission(submission) && (
                    <IconButton
                      onClick={() => handleDeleteClick(submission)}
                      color="error"
                      size="small"
                      aria-label="Delete submission"
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Content */}
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                  mb: 2
                }}
              >
                {submission.content}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* Workflow Status */}
              <SubmissionWorkflowStatus 
                submission={submission} 
                showDetails={true}
                compact={false}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* View Details Dialog */}
      <Dialog
        open={!!viewingSubmission}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Submission Details</DialogTitle>
        <DialogContent>
          {viewingSubmission && (
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Chip
                  label={viewingSubmission.type}
                  color={viewingSubmission.type === 'highlight' ? 'success' : 'warning'}
                  size="small"
                />
                <Typography variant="caption" color="text.secondary">
                  Created: {formatDate(viewingSubmission.createdAt)}
                </Typography>
              </Box>
              
              {/* Penholder Feedback - Prominent Display */}
              {viewingSubmission.penholderFeedback && (
                <Alert 
                  severity={viewingSubmission.workflowState === 'edited' ? 'warning' : 'info'} 
                  sx={{ mb: 3 }}
                  icon={<Edit />}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Penholder Feedback:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewingSubmission.penholderFeedback}
                  </Typography>
                  {viewingSubmission.penholderEditedAt && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Feedback provided: {new Date(viewingSubmission.penholderEditedAt).toLocaleString()}
                    </Typography>
                  )}
                </Alert>
              )}
              
              <Typography variant="subtitle2" gutterBottom>
                Content:
              </Typography>
              <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap', mb: 3 }}>
                {viewingSubmission.content}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Workflow Status:
              </Typography>
              <SubmissionWorkflowStatus 
                submission={viewingSubmission} 
                showDetails={true}
                compact={false}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={!!editingSubmission}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Submission</DialogTitle>
        <DialogContent>
          {/* Penholder Feedback - Prominent Display in Edit Dialog */}
          {editingSubmission?.penholderFeedback && (
            <Alert 
              severity="warning" 
              sx={{ mb: 2 }}
              icon={<Edit />}
            >
              <Typography variant="subtitle2" gutterBottom>
                Penholder Feedback - Please address these comments:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {editingSubmission.penholderFeedback}
              </Typography>
              {editingSubmission.penholderEditedAt && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Feedback provided: {new Date(editingSubmission.penholderEditedAt).toLocaleString()}
                </Typography>
              )}
            </Alert>
          )}
          
          <Alert severity="info" sx={{ mb: 2 }}>
            Note: Editing will reset the workflow status and require re-review by the penholder.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Content"
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            disabled={updateLoading}
            sx={{ mt: 1 }}
            helperText={`${editContent.length}/2000 characters`}
            error={editContent.length > 2000}
          />
          {validationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationError}
            </Alert>
          )}
          <RetryWrapper
            error={updateError}
            loading={updateLoading}
            onRetry={executeUpdate}
          >
            <></>
          </RetryWrapper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} disabled={updateLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={updateLoading || !editContent.trim() || editContent.length > 2000}
            startIcon={updateLoading ? <CircularProgress size={20} /> : null}
          >
            {updateLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={!!deleteSubmission}
        onClose={handleCloseDialogs}
      >
        <DialogTitle>Delete Submission</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {deleteSubmission?.type}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
          {validationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationError}
            </Alert>
          )}
          <RetryWrapper
            error={deleteError}
            loading={deleteLoading}
            onRetry={executeDelete}
          >
            <></>
          </RetryWrapper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubmissionList;